import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';
import { CONFIG_URLS_API } from '../../environments/config-envs';
import { environment } from '../../environments/environment';
import { LoginModel } from '../model/login.model';
import { LocalStorageService } from '../services/localstorage.service';
import { SnackBarService } from '../services/snack-bar-service.service';
import {
  AdmRedePosto,
  GrupoRede,
  GrupoRedeFilter,
  LogMasterPosto,
  PerfisMasterInterface,
  PostosMasterInterface,
  UsuarioMasterInterface,
} from './macro-posto.interface';
import { ApiResponse } from './models/api-response.model';

@Injectable({
  providedIn: 'root',
})
export class AdmRedePostosServices {
  private http = inject(HttpClient);
  private localStorageService = inject(LocalStorageService);
  private readonly snack = inject(SnackBarService);
  public usuario!: LoginModel;

  constructor() {
    var userData = this.localStorageService.getItem('userData');
    if (userData != null) {
      this.usuario = JSON.parse(userData);
    } else {
      this.usuario = {} as LoginModel;
    }
  }

  private getHeaders(): { headers: HttpHeaders } {
    return {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        userGuid: this.usuario?.retorno?.guid,
      }),
    };
  }

  private showError(messages: string[]): void {
    let mensagensConcatenadas = messages.join(' ');
    this.snack.flashMessage(
      mensagensConcatenadas || 'Erro na requisição ao carregar as redes',
      ['snackbar-error'],
      'Fechar',
      3000
    );
  }

  private handleHttpError<T>(
    error: HttpErrorResponse,
    defaultMessage: string
  ): Observable<ApiResponse<T>> {
    let errorMessages: string[] = [];

    if (
      error.error &&
      error.error.mensagens &&
      Array.isArray(error.error.mensagens)
    ) {
      errorMessages = error.error.mensagens;
    } else if (error.error && error.error.message) {
      errorMessages = [error.error.message];
    } else if (error.message) {
      errorMessages = [error.message];
    } else {
      errorMessages = [
        `${defaultMessage} (Status: ${error.status} ${error.statusText || ''})`,
      ];
    }

    this.showError(errorMessages);
    return of({
      sucesso: false,
      retorno: null,
      mensagens: errorMessages,
    } as ApiResponse<T>);
  }

  buscarListaGrupoRede(filtro: GrupoRedeFilter): Observable<GrupoRede[]> {
    let url =
      environment.host_ctf_service + CONFIG_URLS_API.API_BuscarGrupoRede;
    return this.http
      .post<ApiResponse<GrupoRede[]>>(url, filtro, this.getHeaders())
      .pipe(
        catchError((error: HttpErrorResponse) =>
          this.handleHttpError<GrupoRede[]>(
            error,
            'Erro ao buscar grupos de rede'
          )
        ),
        map((response) => {
          if (!response || !response.sucesso) {
            if (response && !response.sucesso) {
              this.showError(
                response.mensagens || ['Erro ao buscar grupos de rede']
              );
            }
            return [];
          }
          return response.retorno || [];
        })
      );
  }

  criarAdmDeRede(
    novaMaster: AdmRedePosto
  ): Observable<ApiResponse<AdmRedePosto>> {
    let url =
      environment.host_ctf_service + CONFIG_URLS_API.API_CriarAdmRedePosto;
    return this.http
      .post<ApiResponse<AdmRedePosto>>(url, novaMaster, this.getHeaders())
      .pipe(
        catchError((error: HttpErrorResponse) =>
          this.handleHttpError<AdmRedePosto>(
            error,
            'Erro ao criar administrador de rede'
          )
        ),
        map((response) => {
          if (response && !response.sucesso) {
            this.showError(
              response.mensagens || ['Erro ao criar administrador de rede']
            );
          }
          return (
            response ||
            ({
              sucesso: false,
              retorno: null,
              mensagens: [],
            } as ApiResponse<AdmRedePosto>)
          );
        })
      );
  }

  desativarLogin(payload: any): Observable<ApiResponse<any>> {
    let url =
      environment.host_ctf_service + CONFIG_URLS_API.API_DesativarUsuarioMaster;
    return this.http
      .put<ApiResponse<any>>(url, payload, this.getHeaders())
      .pipe(
        catchError((error: HttpErrorResponse) =>
          this.handleHttpError<any>(error, 'Erro ao desativar usuário')
        ),
        map((response) => {
          if (response && !response.sucesso) {
            this.showError(response.mensagens || ['Erro ao desativar usuário']);
          }
          return (
            response ||
            ({
              sucesso: false,
              retorno: null,
              mensagens: [],
            } as ApiResponse<any>)
          );
        })
      );
  }

  atualizarUsuarioMaster(usuarioEditado: any): Observable<ApiResponse<any>> {
    let url =
      environment.host_ctf_service + CONFIG_URLS_API.API_EditarUsuarioMaster;
    return this.http
      .put<ApiResponse<any>>(url, usuarioEditado, this.getHeaders())
      .pipe(
        catchError((error: HttpErrorResponse) =>
          this.handleHttpError<any>(error, 'Erro ao atualizar usuário')
        ),
        map((response) => {
          if (response && !response.sucesso) {
            this.showError(response.mensagens || ['Erro ao atualizar usuário']);
          }
          return (
            response ||
            ({
              sucesso: false,
              retorno: null,
              mensagens: [],
            } as ApiResponse<any>)
          );
        })
      );
  }

  criarNovoUsuario(novoUsuario: any): Observable<ApiResponse<any>> {
    let url =
      environment.host_ctf_service + CONFIG_URLS_API.API_CriarUsuarioMaster;
    return this.http
      .post<ApiResponse<any>>(url, novoUsuario, this.getHeaders())
      .pipe(
        catchError((error: HttpErrorResponse) =>
          this.handleHttpError<any>(error, 'Erro ao criar usuário')
        ),
        map((response) => {
          if (response && !response.sucesso) {
            this.showError(response.mensagens || ['Erro ao criar usuário']);
          }
          return (
            response ||
            ({
              sucesso: false,
              retorno: null,
              mensagens: [],
            } as ApiResponse<any>)
          );
        })
      );
  }

  buscarListaAdmRedePosto(): Observable<AdmRedePosto[]> {
    let url =
      environment.host_ctf_service + CONFIG_URLS_API.API_BuscarListaMaster;
    return this.http
      .get<ApiResponse<AdmRedePosto[]>>(url, this.getHeaders())
      .pipe(
        catchError((error: HttpErrorResponse) =>
          this.handleHttpError<AdmRedePosto[]>(
            error,
            'Erro ao buscar lista de administradores de rede'
          )
        ),
        map((response) => {
          if (!response || !response.sucesso) {
            if (response && !response.sucesso) {
              this.showError(
                response.mensagens || [
                  'Erro ao buscar lista de administradores de rede',
                ]
              );
            }
            return [];
          }
          return response.retorno || [];
        })
      );
  }

  editarAdmRedePost(masterEditada: AdmRedePosto): Observable<ApiResponse<any>> {
    let url = environment.host_ctf_service + CONFIG_URLS_API.API_EditarMaster;
    return this.http
      .put<ApiResponse<any>>(url, masterEditada, this.getHeaders())
      .pipe(
        catchError((error: HttpErrorResponse) =>
          this.handleHttpError<any>(
            error,
            'Erro ao editar administrador de rede'
          )
        ),
        map((response) => {
          if (response && !response.sucesso) {
            this.showError(
              response.mensagens || ['Erro ao editar administrador de rede']
            );
          }
          return (
            response ||
            ({
              sucesso: false,
              retorno: null,
              mensagens: [],
            } as ApiResponse<any>)
          );
        })
      );
  }

  buscarListaPerfis(idMaster: number): Observable<PerfisMasterInterface[]> {
    let url =
      environment.host_ctf_service + CONFIG_URLS_API.API_BuscarPerfilPorEmpresa;
    return this.http
      .get<ApiResponse<PerfisMasterInterface[]>>(
        `${url}/${idMaster}`,
        this.getHeaders()
      )
      .pipe(
        catchError((error: HttpErrorResponse) =>
          this.handleHttpError<PerfisMasterInterface[]>(
            error,
            'Erro ao buscar perfis'
          )
        ),
        map((response) => {
          if (!response || !response.sucesso) {
            if (response && !response.sucesso) {
              this.showError(response.mensagens || ['Erro ao buscar perfis']);
            }
            return [];
          }
          return response.retorno || [];
        })
      );
  }

  buscarListaPostosAsync(
    master: AdmRedePosto
  ): Observable<PostosMasterInterface[]> {
    let url =
      environment.host_ctf_service +
      CONFIG_URLS_API.API_BuscarListaPostosAdmRede;
    return this.http
      .post<ApiResponse<PostosMasterInterface[]>>(
        url,
        master,
        this.getHeaders()
      )
      .pipe(
        catchError((error: HttpErrorResponse) =>
          this.handleHttpError<PostosMasterInterface[]>(
            error,
            'Erro ao buscar lista de postos'
          )
        ),
        map((response) => {
          if (!response || !response.sucesso) {
            if (response && !response.sucesso) {
              this.showError(
                response.mensagens || ['Erro ao buscar lista de postos']
              );
            }
            return [];
          }
          return response.retorno || [];
        })
      );
  }

  buscarListaUsuario(busca: any): Observable<UsuarioMasterInterface[]> {
    let url =
      environment.host_ctf_service + CONFIG_URLS_API.API_BuscarListaUsuario;
    return this.http
      .post<ApiResponse<UsuarioMasterInterface[]>>(
        url,
        busca,
        this.getHeaders()
      )
      .pipe(
        catchError((error: HttpErrorResponse) =>
          this.handleHttpError<UsuarioMasterInterface[]>(
            error,
            'Erro ao buscar lista de usuários'
          )
        ),
        map((response) => {
          if (!response || !response.sucesso) {
            if (response && !response.sucesso) {
              this.showError(
                response.mensagens || ['Erro ao buscar lista de usuários']
              );
            }
            return [];
          }
          return response.retorno || [];
        })
      );
  }

  salvarAlteracoesPosto(payload: any): Observable<ApiResponse<boolean>> {
    let url =
      environment.host_ctf_service + CONFIG_URLS_API.API_AtualizarPostosAdmRede;
    return this.http
      .put<ApiResponse<boolean>>(url, payload, this.getHeaders())
      .pipe(
        catchError((error: HttpErrorResponse) =>
          this.handleHttpError<boolean>(
            error,
            'Erro ao salvar alterações de postos'
          )
        ),
        map((response) => {
          if (response && !response.sucesso) {
            this.showError(
              response.mensagens || ['Erro ao salvar alterações de postos']
            );
          }
          return (
            response ||
            ({
              sucesso: false,
              retorno: null,
              mensagens: [],
            } as ApiResponse<boolean>)
          );
        })
      );
  }

  pegarListaLogs(): Observable<LogMasterPosto[]> {
    let url =
      environment.host_ctf_service + CONFIG_URLS_API.API_BuscarListaLogs;
    return this.http
      .get<ApiResponse<LogMasterPosto[]>>(url, this.getHeaders())
      .pipe(
        catchError((error: HttpErrorResponse) =>
          this.handleHttpError<LogMasterPosto[]>(error, 'Erro ao buscar logs')
        ),
        map((response) => {
          if (!response || !response.sucesso) {
            if (response && !response.sucesso) {
              this.showError(response.mensagens || ['Erro ao buscar logs']);
            }
            return [];
          }
          return response.retorno || [];
        })
      );
  }

  gerarNomeArquivo(telaChamando: string): string {
    const agora = new Date();

    const ano = agora.getFullYear();
    const mes = String(agora.getMonth() + 1).padStart(2, '0');
    const dia = String(agora.getDate()).padStart(2, '0');
    const hora = String(agora.getHours()).padStart(2, '0');
    const minuto = String(agora.getMinutes()).padStart(2, '0');
    // const usuario = this.usuario?.retorno?.codigoEntidade || ' ';
    const timestamp = `${ano}${mes}${dia}${hora}${minuto}`;
    const nomeArquivo = `${telaChamando}${timestamp}`;

    return nomeArquivo;
  }
}
